import {
  Component,
  OnInit,
  signal,
  computed
} from '@angular/core';

import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors
} from '@angular/forms';

import { CustomUserService } from 'app/services/user/custom-user.service';
import { RolesService } from 'app/services/role/role-service';
import { AuthService } from 'app/core/auth/auth.service';
import { PermissionDefinitions } from 'app/core/auth/permission-definitions';
import {
  CreateUserDto,
  UpdateUserDto,
  UserDto,
  UserFormMode
} from 'app/models/user';
import { RoleDto } from 'app/models/role';


@Component({
  selector: 'app-custom-users',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './custom-users.component.html',
  styleUrls: ['./custom-users.component.scss']
})
export class CustomUsersComponent implements OnInit {

  // ============================================================
  // Permission Definitions (Exposed to HTML)
  // ============================================================

  readonly Perm = PermissionDefinitions;

  // ============================================================
  // State
  // ============================================================

  users = signal<UserDto[]>([]);
  isLoading = signal(false);
  saving = signal(false);

  // ============================================================
  // Pagination
  // ============================================================

  currentPage = signal(1);
  itemsPerPage = signal(10);
  totalItems = signal(0);
  totalPages = signal(0);

  // ============================================================
  // Filters
  // ============================================================

  searchTerm = signal('');
  userTypeFilter = signal('');
  statusFilter = signal<number | null>(null);
  bannedFilter = signal<number | null>(null);

  userTypes = signal<string[]>(['Admin', 'User', 'Supplier', 'Customer']);

  // ============================================================
  // Create / Edit Modal
  // ============================================================

  showUserModal = signal(false);
  userFormMode = signal<UserFormMode>('create');
  editingUserId = signal<number | null>(null);
  userForm: FormGroup;

  // ============================================================
  // Roles
  // ============================================================

  allRoles = signal<RoleDto[]>([]);
  selectedRoleIds = signal<number[]>([]);

  // ============================================================
  // Delete Modal
  // ============================================================

  showDeleteModal = signal(false);
  pendingDeleteUser = signal<UserDto | null>(null);

  // ============================================================
  // Password Modal
  // ============================================================

  showPasswordModal = signal(false);
  passwordUser = signal<UserDto | null>(null);
  passwordForm: FormGroup;

  // ============================================================
  // Computed
  // ============================================================

  startIndex = computed(() => {
    const total = this.totalItems();
    return total === 0 ? 0 : (this.currentPage() - 1) * this.itemsPerPage() + 1;
  });

  endIndex = computed(() => {
    const end = this.currentPage() * this.itemsPerPage();
    return Math.min(end, this.totalItems());
  });

  isEditMode = computed(() => this.userFormMode() === 'edit');

  modalTitle = computed(() =>
    this.isEditMode() ? 'Edit User' : 'Create New User'
  );

  hasActiveFilters = computed(() =>
    !!this.searchTerm() ||
    !!this.userTypeFilter() ||
    this.statusFilter() !== null ||
    this.bannedFilter() !== null
  );

  /**
   * ✅ هل اليوزر عنده صلاحية عرض المستخدمين؟
   */
  canViewUsersSignal = computed(() =>
    this.auth.hasPermission(this.Perm.Users.View)
  );

  // ============================================================
  // Constructor
  // ============================================================

  constructor(
    private customUserService: CustomUserService,
    private rolesService: RolesService,
    private fb: FormBuilder,
    public auth: AuthService
  ) {
    this.userForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(255)]],
      email: ['', [Validators.email, Validators.maxLength(255)]],
      phone: ['', [Validators.maxLength(20)]],
      password: [''],
      userType: ['User', [Validators.required]],
      address: ['', [Validators.maxLength(500)]],
      country: ['', [Validators.maxLength(100)]],
      state: ['', [Validators.maxLength(100)]],
      city: ['', [Validators.maxLength(100)]],
      postalCode: ['', [Validators.maxLength(20)]],
      balance: [0, [Validators.min(0)]]
    });

    this.passwordForm = this.fb.group({
      newPassword: ['', [Validators.required, Validators.minLength(6)]]
    });
  }

  // ============================================================
  // Init
  // ============================================================

  ngOnInit(): void {
    this.printTokenInfo();

    // ✅ حمّل البيانات بس لو عنده صلاحية
    if (this.canViewUsers()) {
      this.loadPage(1);
      this.loadAllRoles();
      this.loadUserTypes();
    }
  }

  // ============================================================
  // Permission Helpers
  // ============================================================

  canViewUsers(): boolean {
    return this.auth.hasPermission(this.Perm.Users.View);
  }

  canCreateUser(): boolean {
    return this.auth.hasPermission(this.Perm.Users.Create);
  }

  canUpdateUser(): boolean {
    return this.auth.hasPermission(this.Perm.Users.Update);
  }

  canDeleteUser(): boolean {
    return this.auth.hasPermission(this.Perm.Users.Delete);
  }

  canBanUser(): boolean {
    return this.auth.hasPermission(this.Perm.Users.Ban);
  }

  canChangePassword(): boolean {
    return this.auth.hasPermission(this.Perm.Users.ChangePassword);
  }

  canAssignRole(): boolean {
    return this.auth.hasPermission(this.Perm.Users.AssignRole);
  }

  canChangeBalance(): boolean {
    return this.auth.hasPermission(this.Perm.Users.ChangeBalance);
  }

  // ============================================================
  // Password Validators Helper
  // ============================================================

  private applyPasswordValidators(isCreate: boolean): void {
    const passwordControl = this.userForm.get('password');
    if (!passwordControl) return;

    if (isCreate) {
      passwordControl.setValidators([
        Validators.required,
        Validators.minLength(6)
      ]);
    } else {
      passwordControl.clearValidators();
    }

    passwordControl.updateValueAndValidity();
  }

  // ============================================================
  // Load
  // ============================================================

  loadPage(page: number): void {
    // ✅ حماية إضافية
    if (!this.canViewUsers()) return;

    if (this.isLoading()) return;

    this.isLoading.set(true);
    this.currentPage.set(page);

    this.customUserService.getAll({
      pageNumber: page,
      pageSize: this.itemsPerPage(),
      search: this.searchTerm(),
      userType: this.userTypeFilter() || undefined,
      status: this.statusFilter() ?? undefined,
      banned: this.bannedFilter() ?? undefined
    }).subscribe({
      next: (res) => {
        const data = res?.data;

        this.users.set(data?.items ?? []);
        this.totalItems.set(data?.total_count ?? 0);
        this.totalPages.set(
          data?.total_pages ?? Math.ceil((data?.total_count ?? 0) / this.itemsPerPage())
        );

        this.isLoading.set(false);
      },
      error: (err) => {
        console.error('Load users error:', err);
        this.users.set([]);
        this.totalItems.set(0);
        this.totalPages.set(0);
        this.isLoading.set(false);
      }
    });
  }

  loadAllRoles(): void {
    this.rolesService.getAll({ pageNumber: 1, pageSize: 100 }).subscribe({
      next: (res) => this.allRoles.set(res?.data?.items ?? []),
      error: () => this.allRoles.set([])
    });
  }

  loadUserTypes(): void {
    this.customUserService.getUserTypes().subscribe({
      next: (res) => {
        const data = res?.data ?? [];
        if (data.length) this.userTypes.set(data);
      }
    });
  }

  // ============================================================
  // Search / Filters
  // ============================================================

  onSearch(event: Event): void {
    const term = ((event.target as HTMLInputElement).value || '').trim();
    this.searchTerm.set(term);
    this.loadPage(1);
  }

  clearSearch(): void {
    if (!this.searchTerm()) return;
    this.searchTerm.set('');
    this.loadPage(1);
  }

  onUserTypeChange(event: Event): void {
    this.userTypeFilter.set((event.target as HTMLSelectElement).value);
    this.loadPage(1);
  }

  onStatusChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.statusFilter.set(value === '' ? null : Number(value));
    this.loadPage(1);
  }

  onBannedChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.bannedFilter.set(value === '' ? null : Number(value));
    this.loadPage(1);
  }

  resetFilters(): void {
    this.searchTerm.set('');
    this.userTypeFilter.set('');
    this.statusFilter.set(null);
    this.bannedFilter.set(null);
    this.loadPage(1);
  }

  // ============================================================
  // Pagination
  // ============================================================

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages() || page === this.currentPage()) return;
    this.loadPage(page);
  }

  previousPage(): void {
    if (this.currentPage() <= 1) return;
    this.loadPage(this.currentPage() - 1);
  }

  nextPage(): void {
    if (this.currentPage() >= this.totalPages()) return;
    this.loadPage(this.currentPage() + 1);
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisible = 5;
    const total = this.totalPages();
    if (total <= 1) return total === 1 ? [1] : [];
    let start = Math.max(1, this.currentPage() - Math.floor(maxVisible / 2));
    let end = Math.min(total, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }

  onItemsPerPageChange(event: Event): void {
    const value = Number((event.target as HTMLSelectElement).value);
    if (value === this.itemsPerPage()) return;
    this.itemsPerPage.set(value);
    this.loadPage(1);
  }

  // ============================================================
  // Create / Edit
  // ============================================================

  openCreateModal(): void {
    if (!this.canCreateUser()) {
      alert('⛔ You do not have permission to create users');
      return;
    }

    this.userFormMode.set('create');
    this.editingUserId.set(null);
    this.selectedRoleIds.set([]);

    this.applyPasswordValidators(true);

    this.userForm.reset({ userType: 'User', balance: 0 });
    this.showUserModal.set(true);
  }

  openEditModal(user: UserDto): void {
    if (!this.canUpdateUser()) {
      alert('⛔ You do not have permission to update users');
      return;
    }

    this.userFormMode.set('edit');
    this.editingUserId.set(user.id);
    this.selectedRoleIds.set([]);

    this.applyPasswordValidators(false);

    this.customUserService.getById(user.id).subscribe({
      next: (res) => {
        const d = res?.data;
        if (!d) return;

        this.userForm.patchValue({
          name: d.name,
          email: d.email ?? '',
          phone: d.phone ?? '',
          userType: d.user_type,
          address: d.address ?? '',
          country: d.country ?? '',
          state: d.state ?? '',
          city: d.city ?? '',
          postalCode: d.postal_code ?? '',
          balance: d.balance
        });

        this.selectedRoleIds.set((d.roles ?? []).map(r => r.id));
        this.showUserModal.set(true);
      },
      error: () => alert('Failed to load user')
    });
  }

  closeUserModal(): void {
    if (this.saving()) return;
    this.showUserModal.set(false);
    this.userForm.reset({ userType: 'User', balance: 0 });
    this.editingUserId.set(null);
    this.selectedRoleIds.set([]);
  }

  // ============================================================
  // Submit User
  // ============================================================

  submitUser(): void {
    if (this.userForm.invalid) {
      this.userForm.markAllAsTouched();
      console.warn('⚠️ Form invalid:', this.userForm.errors);
      return;
    }

    const v = this.userForm.value;
    this.saving.set(true);

    if (this.isEditMode()) {
      this.submitUpdate(v);
    } else {
      this.submitCreate(v);
    }
  }

  // ============================================================
  // Update User
  // ============================================================

  private submitUpdate(v: any): void {
    const id = this.editingUserId();
    if (!id) { this.saving.set(false); return; }

    const dto: UpdateUserDto = {
      name: v.name.trim(),
      email: v.email?.trim() || undefined,
      phone: v.phone?.trim() || undefined,
      address: v.address?.trim() || undefined,
      country: v.country?.trim() || undefined,
      state: v.state?.trim() || undefined,
      city: v.city?.trim() || undefined,
      postalCode: v.postalCode?.trim() || undefined
    };

    console.log('📤 Update User Payload:', dto);

    this.customUserService.update(id, dto).subscribe({
      next: (res) => {
        console.log('📥 Update Response:', res);

        if (!res.success) {
          alert(res.message || 'Failed to update user');
          this.saving.set(false);
          return;
        }

        if (this.canAssignRole()) {
          this.customUserService.assignRoles(id, { roleIds: this.selectedRoleIds() }).subscribe({
            next: (rolesRes) => {
              console.log('📥 Assign Roles Response:', rolesRes);
              this.saving.set(false);
              this.showUserModal.set(false);
              this.userForm.reset({ userType: 'User', balance: 0 });
              this.editingUserId.set(null);
              this.loadPage(this.currentPage());
            },
            error: (err) => {
              alert(err?.error?.message || 'Failed to assign roles');
              this.saving.set(false);
              this.loadPage(this.currentPage());
            }
          });
        } else {
          this.saving.set(false);
          this.showUserModal.set(false);
          this.userForm.reset({ userType: 'User', balance: 0 });
          this.editingUserId.set(null);
          this.loadPage(this.currentPage());
        }
      },
      error: (err) => {
        console.error('❌ Update user error:', err);
        alert(err?.error?.message || 'Failed to update user');
        this.saving.set(false);
      }
    });
  }

  // ============================================================
  // Create User
  // ============================================================

  private submitCreate(v: any): void {
    const dto: CreateUserDto = {
      name: v.name.trim(),
      email: v.email?.trim() || undefined,
      phone: v.phone?.trim() || undefined,
      password: v.password?.trim() || undefined,
      userType: v.userType || 'User',
      address: v.address?.trim() || undefined,
      country: v.country?.trim() || undefined,
      state: v.state?.trim() || undefined,
      city: v.city?.trim() || undefined,
      postalCode: v.postalCode?.trim() || undefined,
      balance: v.balance ?? 0,
      roleIds: this.canAssignRole() ? this.selectedRoleIds() : []
    };

    console.log('📤 Create User Payload:', dto);

    this.customUserService.create(dto).subscribe({
      next: (res) => {
        console.log('📥 Create Response:', res);

        this.saving.set(false);

        if (!res.success) {
          alert(res.message || 'Failed to create user');
          return;
        }

        this.showUserModal.set(false);
        this.userForm.reset({ userType: 'User', balance: 0 });
        this.loadPage(1);
      },
      error: (err) => {
        console.error('❌ Create user error:', err);
        alert(err?.error?.message || 'Failed to create user');
        this.saving.set(false);
      }
    });
  }

  // ============================================================
  // Delete
  // ============================================================

  confirmDelete(user: UserDto): void {
    if (!this.canDeleteUser()) {
      alert('⛔ You do not have permission to delete users');
      return;
    }

    this.pendingDeleteUser.set(user);
    this.showDeleteModal.set(true);
  }

  closeDeleteModal(): void {
    this.showDeleteModal.set(false);
    this.pendingDeleteUser.set(null);
  }

  deleteUser(): void {
    const user = this.pendingDeleteUser();
    if (!user) return;

    this.saving.set(true);
    this.customUserService.delete(user.id).subscribe({
      next: (res) => {
        this.saving.set(false);
        if (res.success) {
          this.closeDeleteModal();
          this.loadPage(this.currentPage());
        }
      },
      error: (err) => {
        console.error('Delete user error:', err);
        alert(err?.error?.message || 'Failed to delete user');
        this.saving.set(false);
      }
    });
  }

  // ============================================================
  // Status / Ban
  // ============================================================

  toggleBan(user: UserDto): void {
    if (!this.canBanUser()) {
      alert('⛔ You do not have permission to ban users');
      return;
    }

    const newValue = user.banned === 1 ? 0 : 1;
    this.customUserService.changeStatus(user.id, { banned: newValue }).subscribe({
      next: () => this.loadPage(this.currentPage()),
      error: (err) => alert(err?.error?.message || 'Failed to update user')
    });
  }

  toggleStatus(user: UserDto): void {
    if (!this.canBanUser()) {
      alert('⛔ You do not have permission to change user status');
      return;
    }

    const newValue = user.status === 1 ? 0 : 1;
    this.customUserService.changeStatus(user.id, { status: newValue }).subscribe({
      next: () => this.loadPage(this.currentPage()),
      error: (err) => alert(err?.error?.message || 'Failed to update user')
    });
  }

  // ============================================================
  // Password
  // ============================================================

  openPasswordModal(user: UserDto): void {
    if (!this.canChangePassword()) {
      alert('⛔ You do not have permission to change passwords');
      return;
    }

    this.passwordUser.set(user);
    this.passwordForm.reset();
    this.showPasswordModal.set(true);
  }

  closePasswordModal(): void {
    if (this.saving()) return;
    this.showPasswordModal.set(false);
    this.passwordUser.set(null);
    this.passwordForm.reset();
  }

  submitPassword(): void {
    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();
      return;
    }

    const user = this.passwordUser();
    if (!user) return;

    this.saving.set(true);

    this.customUserService.changePassword(user.id, {
      newPassword: this.passwordForm.value.newPassword
    }).subscribe({
      next: (res) => {
        this.saving.set(false);
        if (res.success) {
          alert('✅ Password changed successfully');
          this.closePasswordModal();
        }
      },
      error: (err) => {
        console.error('Change password error:', err);
        alert(err?.error?.message || 'Failed to change password');
        this.saving.set(false);
      }
    });
  }

  // ============================================================
  // Roles
  // ============================================================

  toggleRole(roleId: number): void {
    if (!this.canAssignRole()) {
      alert('⛔ You do not have permission to assign roles');
      return;
    }

    const current = this.selectedRoleIds();
    if (current.includes(roleId)) {
      this.selectedRoleIds.set(current.filter(x => x !== roleId));
    } else {
      this.selectedRoleIds.set([...current, roleId]);
    }
  }

  isRoleSelected(roleId: number): boolean {
    return this.selectedRoleIds().includes(roleId);
  }

  // ============================================================
  // Helpers — Error Messages
  // ============================================================

  getFieldError(fieldName: string): string {
    const control = this.userForm.get(fieldName);
    if (!control || !control.errors || !control.touched) return '';

    const errors = control.errors;

    if (errors['required']) {
      return `${this.getFieldLabel(fieldName)} is required`;
    }

    if (errors['email']) {
      return 'Please enter a valid email address';
    }

    if (errors['minlength']) {
      const required = errors['minlength'].requiredLength;
      const actual = errors['minlength'].actualLength;
      return `Minimum ${required} characters (you entered ${actual})`;
    }

    if (errors['maxlength']) {
      const required = errors['maxlength'].requiredLength;
      return `Maximum ${required} characters`;
    }

    if (errors['min']) {
      return `Minimum value is ${errors['min'].min}`;
    }

    return 'Invalid value';
  }

  hasFieldError(fieldName: string): boolean {
    const control = this.userForm.get(fieldName);
    return !!(control && control.invalid && (control.touched || control.dirty));
  }

  private getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      password: 'Password',
      userType: 'User Type',
      address: 'Address',
      country: 'Country',
      state: 'State',
      city: 'City',
      postalCode: 'Postal Code',
      balance: 'Balance'
    };
    return labels[fieldName] || fieldName;
  }

  // ============================================================
  // Debug — Print Token Info
  // ============================================================

  printTokenInfo(): void {
    const payload = this.auth.getPayload();

    console.log(
      '%c🔍 ============== TOKEN INSPECT (USERS) ==============',
      'color: #6366f1; font-weight: bold; font-size: 13px;'
    );

    console.log('%c📦 Full Payload:', 'color: #6366f1; font-weight: bold;', payload);

    console.log('%c👤 User Info:', 'color: #2563eb; font-weight: bold;');
    console.log('   ├─ ID:', this.auth.userId);
    console.log('   ├─ Name:', this.auth.userName);
    console.log('   ├─ Email:', this.auth.userEmail);
    console.log('   └─ Phone:', this.auth.userPhone);

    const roles = this.auth.getRoles();
    console.log(
      `%c🎭 Roles (${roles.length}):`,
      'color: #7c3aed; font-weight: bold;',
      roles
    );

    const permissions = this.auth.getPermissions();
    console.log(
      `%c🔐 Permissions (${permissions.length}):`,
      'color: #059669; font-weight: bold;',
      permissions
    );

    console.log('%c✅ Users Permission Checks:', 'color: #dc2626; font-weight: bold;');
    console.log('   ├─ Can View Users?', this.canViewUsers());
    console.log('   ├─ Can Create Users?', this.canCreateUser());
    console.log('   ├─ Can Update Users?', this.canUpdateUser());
    console.log('   ├─ Can Delete Users?', this.canDeleteUser());
    console.log('   ├─ Can Ban Users?', this.canBanUser());
    console.log('   ├─ Can Change Password?', this.canChangePassword());
    console.log('   ├─ Can Assign Role?', this.canAssignRole());
    console.log('   └─ Can Change Balance?', this.canChangeBalance());

    console.log(
      '%c=====================================================',
      'color: #6366f1; font-weight: bold; font-size: 13px;'
    );
  }

  // ============================================================
  // Helpers
  // ============================================================

  getUserInitial(user: UserDto): string {
    return (user.name || '?').charAt(0).toUpperCase();
  }

  getUserTypeBadgeClass(type: string): string {
    switch (type?.toLowerCase()) {
      case 'admin': return 'badge-admin';
      case 'supplier': return 'badge-supplier';
      case 'customer': return 'badge-customer';
      default: return 'badge-user';
    }
  }
}